import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar, backLink } from "../components/common.js";
import { spinner, errorState, toast, confirmDialog, openModal } from "../components/ui.js";
import { programCoverage, openVolumeInfo } from "../components/volumeCoverage.js";

export default function ProgramEditor(params) {
  const root = h("div", { class: "screen" });
  const programId = params.id || null;
  const isNew = !programId;

  let name = "";
  let description = "";
  let days = []; // [{ workout_id, workout_name, label }]
  let workouts = [];
  const daysWrap = h("div");
  const coverageWrap = h("div");

  load();

  async function load() {
    setChildren(root, topbar(isNew ? "New program" : "Edit program"), spinner());
    try {
      workouts = await api.workouts();
      if (!isNew) {
        const p = await api.program(programId);
        name = p.name;
        description = p.description || "";
        days = p.days.map((d) => ({
          workout_id: d.workout_id,
          workout_name: d.workout ? d.workout.name : null,
          label: d.label || (d.workout ? null : "Rest"),
        }));
      }
      render();
    } catch (e) {
      setChildren(root, topbar("Program"), errorState(e.message, load));
    }
  }

  function render() {
    const nameInput = h("input", { class: "input", id: "prog-name", value: name, placeholder: "e.g. Push / Pull / Legs", oninput: (e) => (name = e.target.value) });
    const descInput = h("textarea", { class: "input", id: "prog-desc", rows: 2, placeholder: "Optional — what's this block for?", oninput: (e) => (description = e.target.value) }, description);

    renderDays();
    setChildren(root,
      backLink("Programs", "/programs"),
      topbar(isNew ? "New program" : "Edit program"),
      h("div", { class: "card" },
        h("div", { class: "field" }, h("label", null, "Name"), nameInput),
        h("div", { class: "field" }, h("label", null, "Description"), descInput)
      ),
      h("div", { class: "section-title" }, "Schedule"),
      h("p", { class: "dim", style: { fontSize: ".82rem", marginTop: "-4px" } }, "Add your workouts and rest days in order. The schedule repeats, so a week (or several) becomes an ongoing cycle."),
      daysWrap,
      h("div", { class: "btn-row mt" },
        h("button", { class: "btn btn-ghost grow", onClick: addWorkoutDay }, icon("plus", { size: 18 }), "Workout day"),
        h("button", { class: "btn btn-ghost grow", onClick: addRestDay }, icon("plus", { size: 18 }), "Rest day")
      ),
      h("div", { class: "section-title row-between" },
        h("span", null, "Projected muscle coverage"),
        h("button", { class: "link-btn dim", style: { fontSize: ".82rem", display: "inline-flex", alignItems: "center", gap: "4px" }, onClick: openVolumeInfo },
          icon("info", { size: 14 }), "What's this?")
      ),
      h("div", { class: "card" }, coverageWrap),
      h("button", { class: "btn btn-primary btn-block btn-lg mt-lg", onClick: save }, icon("check", { size: 20 }), "Save program"),
      !isNew ? h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: del }, icon("trash", { size: 18 }), "Delete program") : null
    );
  }

  function renderDays() {
    renderCoverage();
    if (!days.length) {
      setChildren(daysWrap, h("div", { class: "card center" }, h("p", { class: "dim" }, "No days yet — add a workout or rest day to start.")));
      return;
    }
    const rows = [];
    days.forEach((d, i) => {
      if (i % 7 === 0) rows.push(h("div", { class: "section-title", style: { margin: "10px 0 4px" } }, `Week ${Math.floor(i / 7) + 1}`));
      rows.push(dayRow(d, i));
    });
    setChildren(daysWrap, ...rows);
  }

  // Live projection: map each scheduled day to its workout's exercises (api.workouts() carries
  // target_sets + muscle ids), then hand the schedule to the shared coverage view. Recomputed on
  // every edit since renderDays() is the choke-point for all schedule mutations.
  function renderCoverage() {
    const byId = new Map(workouts.map((w) => [w.id, w]));
    const sched = days.map((d) => ({ exercises: d.workout_id ? (byId.get(d.workout_id)?.exercises || []) : [] }));
    setChildren(coverageWrap, programCoverage(sched, { cycleDays: sched.length }));
  }

  function dayRow(d, i) {
    const rest = !d.workout_id;
    return h("div", { class: "card hstack", style: { marginBottom: "8px", alignItems: "center", gap: "10px" } },
      h("div", { class: "set-num", style: { flex: "0 0 auto" } }, `D${i + 1}`),
      h("div", { class: "grow" },
        h("div", { style: { fontWeight: 600, color: rest ? "var(--muted)" : "inherit" } }, rest ? (d.label || "Rest day") : d.workout_name || "(removed workout)"),
        h("div", { class: "dim", style: { fontSize: ".78rem" } }, rest ? "Recovery" : "Workout")
      ),
      h("button", { class: "icon-btn", onClick: () => move(i, -1), "aria-label": "Move up", disabled: i === 0 }, icon("back", { size: 16 })),
      h("button", { class: "icon-btn", onClick: () => move(i, 1), "aria-label": "Move down", disabled: i === days.length - 1 }, icon("forward", { size: 16 })),
      h("button", { class: "icon-btn", style: { color: "var(--danger)" }, onClick: () => remove(i), "aria-label": "Remove" }, icon("x", { size: 16 }))
    );
  }

  function move(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= days.length) return;
    [days[i], days[j]] = [days[j], days[i]];
    renderDays();
  }
  function remove(i) {
    days.splice(i, 1);
    renderDays();
  }
  function addRestDay() {
    days.push({ workout_id: null, workout_name: null, label: "Rest" });
    renderDays();
  }
  function addWorkoutDay() {
    if (!workouts.length) {
      toast("Create a workout first", "warn");
      return;
    }
    const m = openModal({
      title: "Add a workout day",
      content: h("div", null,
        ...workouts.map((w) =>
          h("button", { class: "btn btn-ghost btn-block", style: { marginBottom: "8px", justifyContent: "flex-start" }, onClick: () => {
            days.push({ workout_id: w.id, workout_name: w.name, label: null });
            m.close();
            renderDays();
          } }, h("span", { style: { fontWeight: 600 } }, w.name), h("span", { class: "dim", style: { marginLeft: "8px", fontSize: ".82rem" } }, `${w.exercises.length} ex`))
        )
      ),
    });
  }

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast("Give your program a name", "warn");
      return;
    }
    const payloadDays = days.map((d) => ({ workout_id: d.workout_id || null, label: d.label || null }));
    try {
      let id = programId;
      if (isNew) {
        const created = await api.createProgram({ name: trimmed, description });
        id = created.id;
      } else {
        await api.updateProgram(id, { name: trimmed, description });
      }
      await api.setProgramDays(id, payloadDays);
      toast("Program saved", "success");
      navigate("/programs");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function del() {
    const ok = await confirmDialog({ title: "Delete program?", message: `"${name}" will be removed. Your workouts and history are kept.`, confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteProgram(programId);
      toast("Program deleted", "success");
      navigate("/programs");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  return root;
}
